import { hash01 } from './ink';
import type { Fit } from './view';

export interface BurstSpec {
  readonly count: number;
  readonly speed: number;
  readonly drag: number;
  readonly gravity: number;
  readonly spin: number;
  readonly life: number;
  readonly sizeMin: number;
  readonly sizeMax: number;
}

export interface Bit {
  x: number;
  y: number;
  size: number;
  rot: number;
  alpha: number;
  tone: number;
}

export const FADE_IN = 0.15;
export const MAX_BITS = 150;
export const MILESTONE_BOOST = 2;
export const TRAIL_SECONDS = 0.5;

const TAU = Math.PI * 2;

export function smooth(t: number): number {
  const k = Math.max(0, Math.min(1, Number.isFinite(t) ? t : 0));
  return k * k * (3 - 2 * k);
}

export function burstCount(fit: Fit, milestone: boolean, reduced: boolean, base: number): number {
  if (reduced) return 0;
  const k = fit === 'seat' ? 1 : fit === 'gap' ? 0.6 : fit === 'squash' ? 0.3 : 0;
  if (k === 0) return 0;
  return Math.round(base * k) + (milestone ? base * MILESTONE_BOOST : 0);
}

export function isMilestone(crossed: number | null, before: number, after: number): boolean {
  return crossed !== null || (after >= 4 && after > before);
}

export function fadeOf(age: number, life: number): number {
  if (!(age >= 0) || !(life > 0) || age >= life) return 0;
  return smooth(age / FADE_IN) * (1 - smooth((age - life * 0.45) / (life * 0.55)));
}

export function bitAt(out: Bit, seed: number, i: number, age: number, spec: BurstSpec, from: number, tones: number): boolean {
  if (!(age >= 0)) return false;
  const life = spec.life * (0.7 + 0.3 * hash01(seed, i, 3));
  if (age >= life) return false;
  const angle = hash01(seed, i, 1) * TAU;
  const speed = spec.speed * (0.45 + 0.55 * hash01(seed, i, 2));
  const travel = spec.drag > 0 ? (speed * (1 - Math.exp(-spec.drag * age))) / spec.drag : speed * age;
  const reach = from + travel;
  out.x = Math.cos(angle) * reach;
  out.y = Math.sin(angle) * reach + 0.5 * spec.gravity * age * age;
  out.size = spec.sizeMin + (spec.sizeMax - spec.sizeMin) * hash01(seed, i, 4);
  out.rot = hash01(seed, i, 6) * TAU + (hash01(seed, i, 5) * 2 - 1) * spec.spin * age;
  out.alpha = fadeOf(age, life);
  out.tone = Math.min(tones - 1, Math.floor(hash01(seed, i, 7) * tones));
  return out.alpha > 0;
}

export interface Trail {
  readonly xs: Float64Array;
  readonly ys: Float64Array;
  readonly ts: Float64Array;
  head: number;
  size: number;
}

export function createTrail(capacity: number): Trail {
  const n = Math.max(1, Math.floor(capacity));
  return { xs: new Float64Array(n), ys: new Float64Array(n), ts: new Float64Array(n), head: 0, size: 0 };
}

export function trailPush(trail: Trail, x: number, y: number, t: number, minStep: number): boolean {
  const cap = trail.xs.length;
  if (trail.size > 0) {
    const last = (trail.head - 1 + cap) % cap;
    if (Math.hypot(x - trail.xs[last]!, y - trail.ys[last]!) < minStep) return false;
  }
  trail.xs[trail.head] = x;
  trail.ys[trail.head] = y;
  trail.ts[trail.head] = t;
  trail.head = (trail.head + 1) % cap;
  trail.size = Math.min(cap, trail.size + 1);
  return true;
}

export function trailIndex(trail: Trail, back: number): number {
  const cap = trail.xs.length;
  return (trail.head - 1 - back + cap * 2) % cap;
}

export function trailClear(trail: Trail): void {
  trail.head = 0;
  trail.size = 0;
}
